import React, { useEffect, useRef } from 'react';
import type { CallState, TranscriptEntry } from './useVoiceCall';
import './VoiceCallScreen.css';

interface VoiceCallScreenProps {
  callState: CallState;
  isMuted: boolean;
  callDuration: number;
  transcript: TranscriptEntry[];
  onEndCall: () => void;
  onToggleMute: () => void;
  error: string | null;
  onRetry: () => void;
}

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const VoiceCallScreen: React.FC<VoiceCallScreenProps> = ({
  callState,
  isMuted,
  callDuration,
  transcript,
  onEndCall,
  onToggleMute,
  error,
  onRetry,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll transcript to bottom
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // Audio-reactive waveform visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);

    const w = () => canvas.offsetWidth;
    const h = () => canvas.offsetHeight;

    let phase = 0;
    const isActive = callState === 'active';
    const isConnecting = callState === 'connecting';

    const draw = () => {
      ctx.clearRect(0, 0, w(), h());
      const cx = w() / 2;
      const cy = h() / 2;
      phase += isActive ? 0.03 : 0.015;

      const layers = isActive ? 3 : 2;
      for (let layer = 0; layer < layers; layer++) {
        const alpha = isActive ? 0.15 + layer * 0.1 : 0.08 + layer * 0.06;
        const amplitude = isActive
          ? 30 + layer * 18 + Math.sin(phase * 1.3 + layer) * 12
          : 10 + layer * 6 + Math.sin(phase + layer) * 4;

        const grad = ctx.createLinearGradient(0, cy - 60, 0, cy + 60);
        grad.addColorStop(0, `rgba(99, 102, 241, ${alpha})`);
        grad.addColorStop(0.5, `rgba(139, 92, 246, ${alpha + 0.05})`);
        grad.addColorStop(1, `rgba(59, 130, 246, ${alpha})`);

        ctx.beginPath();
        ctx.moveTo(0, cy);
        const segments = 120;
        for (let i = 0; i <= segments; i++) {
          const x = (i / segments) * w();
          const t = (i / segments) * Math.PI * 4;
          const wave1 = Math.sin(t + phase + layer * 0.8) * amplitude;
          const wave2 = Math.sin(t * 1.5 + phase * 0.7 + layer) * (amplitude * 0.4);
          const envelope = Math.sin((i / segments) * Math.PI);
          const y = cy + (wave1 + wave2) * envelope;
          ctx.lineTo(x, y);
        }
        ctx.strokeStyle = grad;
        ctx.lineWidth = isActive ? 2.5 - layer * 0.3 : 1.5;
        ctx.stroke();
      }

      if (isConnecting) {
        const pulseAlpha = 0.15 + Math.sin(phase * 2) * 0.1;
        const pulseRadius = 40 + Math.sin(phase * 1.5) * 15;
        const radGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, pulseRadius);
        radGrad.addColorStop(0, `rgba(139, 92, 246, ${pulseAlpha})`);
        radGrad.addColorStop(1, 'rgba(139, 92, 246, 0)');
        ctx.fillStyle = radGrad;
        ctx.fillRect(0, 0, w(), h());
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [callState]);

  return (
    <div className="voice-screen">
      {/* Gradient background glow */}
      <div className="voice-screen-glow" />

      {/* Waveform canvas */}
      <canvas ref={canvasRef} className="voice-screen-canvas" />

      {/* Status area */}
      <div className="voice-screen-status">
        {callState === 'connecting' && (
          <div className="voice-screen-connecting">
            <div className="voice-connecting-dots">
              <span /><span /><span />
            </div>
            <span className="voice-status-text">Connecting</span>
          </div>
        )}
        {callState === 'active' && (
          <div className="voice-screen-live">
            <span className="voice-live-badge">
              <i className="bi bi-soundwave" /> Live
            </span>
            <span className="voice-timer">{formatDuration(callDuration)}</span>
          </div>
        )}
        {callState === 'ending' && (
          <span className="voice-status-text fading">Ending call...</span>
        )}
      </div>

      {/* Live transcript */}
      {transcript.length > 0 && (
        <div className="voice-transcript">
          <div className="voice-transcript-inner">
            {transcript.map((entry, i) => (
              <div
                key={i}
                className={`voice-transcript-entry ${entry.role} ${entry.interim ? 'interim' : ''}`}
              >
                <span className="voice-transcript-role">
                  {entry.role === 'you' ? 'You' : 'ToothFairy'}:
                </span>{' '}
                <span className="voice-transcript-text">{entry.text}</span>
              </div>
            ))}
            <div ref={transcriptEndRef} />
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && callState === 'error' && (
        <div className="voice-screen-error">
          <i className="bi bi-exclamation-triangle-fill" />
          <span>{error}</span>
          <button onClick={onRetry}>Retry</button>
        </div>
      )}

      {/* Bottom controls */}
      <div className="voice-screen-controls">
        <button
          className={`voice-ctrl-btn mic-btn ${isMuted ? 'muted' : ''}`}
          onClick={onToggleMute}
          disabled={callState !== 'active'}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          <i className={`bi ${isMuted ? 'bi-mic-mute-fill' : 'bi-mic-fill'}`} />
        </button>

        <button
          className="voice-ctrl-btn end-btn"
          onClick={onEndCall}
          disabled={callState === 'ending'}
          aria-label="End call"
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>
    </div>
  );
};

export default VoiceCallScreen;
